import { Button } from "@shared/src/components/ui/button";
import { useAuthStore } from "@shared/src/stores/useAuthStore";
import { useModalStore } from "@shared/src/stores/useModalStore";
import React, { useState, useEffect, useRef } from "react";
import { IoClose } from "react-icons/io5";
import { generalAxios } from "@shared/src/utils/axios";
import Resizer from "react-image-file-resizer";
import { useHostStore } from "@shared/src/stores/useHostStore";
import { CustomSelect } from "@shared/src/components/ui/common/form-components/CustomSelect";
import { CustomInput } from "@shared/src/components/common/form-components/CustomInput";
import { CustomTextArea } from "@shared/src/components/common/form-components/CustomTextArea";
import { ixToast } from "@shared/src/utils/ixToast";

export const uploadFileToCloudinary = async (
  fileArgs: { url: string; type: "image" | "video"; file: File },
  type = "iexchangechat",
) => {
  const url = `https://api.cloudinary.com/v1_1/dy9enqwwq/upload`;
  const formData = new FormData();
  formData.append("file", fileArgs.type === "image" ? fileArgs.url : fileArgs.file);
  formData.append("upload_preset", "iexchange_feedback");

  const response = await generalAxios.post(url, formData);
  return response.data.secure_url;
};

const FeedbackForm: React.FC = () => {
  const [platform, setPlatform] = useState<string>("");
  const { activeAccount } = useAuthStore();
  const [currentUrl, setCurrentUrl] = useState<string>("");
  const { activeChain } = useHostStore();
  const modal = useModalStore();
  const [selectedMedia, setSelectedMedia] = useState<{ url: string; type: "image" | "video"; file: File } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [feedbackType, setFeedbackType] = useState<string>("General");
  const [contactInfo, setContactInfo] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Detect platform and current URL
    setPlatform(/Mobi|Android|iPhone/i.test(navigator.userAgent) ? "mobile" : "web");
    setCurrentUrl(window.location.href);
  }, []);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Add file size check (10MB limit)
    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      modal.showModal({
        type: "error",
        title: "File too large",
        description: "Please select a file smaller than 10MB.",
      });
      return;
    }

    const fileType = file.type.split("/")[0];

    if (fileType === "image") {
      try {
        const resizedImage = await new Promise<string>(resolve => {
          Resizer.imageFileResizer(
            file,
            800,
            800,
            "JPEG",
            80,
            0,
            uri => {
              resolve(uri as string);
            },
            "base64",
          );
        });
        setSelectedMedia({ url: resizedImage, type: "image", file });
      } catch (err) {
        console.error("Error resizing image:", err);
      }
    } else if (fileType === "video") {
      // For videos, we'll use a blob URL for preview
      const blobUrl = URL.createObjectURL(file);
      setSelectedMedia({ url: blobUrl, type: "video", file: file });
    } else {
      modal.showModal({
        type: "error",
        title: "Unsupported file type",
        description: "Please select an image or video file.",
      });
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Get the feedback text and validate it's not empty
    const feedbackText = (
      event.currentTarget.elements.namedItem("entry.2042243173") as HTMLTextAreaElement
    ).value.trim();

    if (!feedbackText) {
      ixToast.error("Please enter your feedback before submitting.");
      return;
    }

    setUploading(true);

    const form = event.currentTarget as HTMLFormElement;
    const formData = new FormData(form);

    // Ensure the feedback type and contact info from state are used
    formData.set("entry.1644956034", feedbackType);
    formData.set("entry.293023654", contactInfo);

    let mediaUrl = null;

    // Upload media to Cloudinary if one is selected
    if (selectedMedia) {
      try {
        mediaUrl = await uploadFileToCloudinary(selectedMedia, "iexchange_feedback");
        console.log("Media uploaded:", selectedMedia);
      } catch (error) {
        console.error("Error uploading media:", error);
        modal.showModal({
          type: "error",
          title: "Upload Failed",
          description: "Failed to upload media. Please try again without an attachment.",
        });
        setUploading(false);
        return;
      }
    }

    // Programmatically populate hidden fields
    formData.set("entry.1230321292", "Example Error Message");
    formData.set("entry.1838589843", activeAccount?.address as string);
    formData.set("entry.1923096368", currentUrl);
    formData.set("entry.178031363", platform);
    formData.set("entry.395100867", activeChain?.name as string);

    // Add the media URL to form data if available
    if (mediaUrl) {
      formData.set("entry.1117937854", mediaUrl);
    }

    try {
      await fetch(
        "https://docs.google.com/forms/d/e/1FAIpQLSfW826aSNUcXOaS-8JiO25JsPS6IKqnSOBZGbojQ2XPWrXuQA/formResponse",
        {
          method: "POST",
          body: formData,
          mode: "no-cors",
        },
      );
      modal.showModal({ type: "success", title: "Thank you!", description: "Your feedback has been submitted." });
      // Reset the form and media state
      setSelectedMedia(null);
      const analytics = useHostStore.getState().analytics;
      analytics?.clarity?.trackEvent("feedback_submitted");
      analytics?.clarity?.trackEvent("feedback_submitted_" + formData.get("entry.1644956034"));
      if (mediaUrl) analytics?.clarity?.trackEvent("Feedback_submitted_with_media");
      form.reset();
    } catch (error) {
      console.error("Error submitting form:", error);
      modal.showModal({
        type: "error",
        title: "Submission Failed",
        description: "Failed to submit feedback. Please try again later.",
      });
    } finally {
      setUploading(false);
    }
  };

  const feedbackTypeOptions = [
    { value: "General", label: "General" },
    { value: "Report an issue", label: "Report an issue" },
    { value: "Share an idea", label: "Share an idea" },
    { value: "Give a compliment", label: "Give a compliment" },
  ];

  return (
    <>
      <div className="w-full pt-5 rounded shadow-lg relative">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <CustomSelect
              label="What kind of feedback do you want to give?"
              value={feedbackType}
              onValueChange={value => setFeedbackType(value as string)}
              options={feedbackTypeOptions}
              required
              showSearch={false}
              variant="dropdown"
            />
            <input type="hidden" name="entry.1644956034" value={feedbackType} />
          </div>

          <div>
            <CustomTextArea id="feedback-text" name="entry.2042243173" rows={4} required label="Your feedback" />
          </div>

          <div className="space-y-1.5">
            <div className="relative w-full rounded-2xl border border-border-tertiary bg-surfaces-bg-primary transition-all duration-200 p-4">
              <label className="absolute -top-2 left-3 !text-small bg-surfaces-bg-primary px-2 text-text-tertiary">
                Attach media (optional)
              </label>
              <div className="flex items-center">
                <input
                  type="file"
                  id="media-upload"
                  ref={fileInputRef}
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                  className="sr-only"
                />
                <Button type="button" variant="secondary" radius="full" onClick={() => fileInputRef.current?.click()}>
                  Choose Media
                </Button>
              </div>

              {/* Media Preview */}
              {selectedMedia && (
                <div className="mt-3 relative w-32 h-32">
                  {selectedMedia.type === "image" ? (
                    <img
                      src={selectedMedia.url}
                      alt="Selected image"
                      className="w-full h-full object-cover rounded-lg"
                      width={128}
                      height={128}
                    />
                  ) : (
                    <video src={selectedMedia.url} className="w-full h-full object-cover rounded-lg" controls />
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedMedia.type === "video") {
                        URL.revokeObjectURL(selectedMedia.url);
                      }
                      setSelectedMedia(null);
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 text-white"
                  >
                    <IoClose size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <CustomInput
              id="contact-info"
              name="entry.293023654"
              label="Email or Telegram (optional)"
              placeholder="Enter your email or Telegram handle"
              value={contactInfo}
              onChange={e => setContactInfo(e.target.value)}
            />
          </div>

          {/* Hidden Fields */}
          <input type="hidden" name="entry.395100867" value="Morph" />
          <input type="hidden" name="entry.1230321292" />
          <input type="hidden" name="entry.1838589843" />
          <input type="hidden" name="entry.1923096368" />
          <input type="hidden" name="entry.178031363" />
          {/* Hidden field for media URL - will be populated during submission */}
          <input type="hidden" name="entry.583948723" />

          <div className="flex justify-end">
            <Button type="submit" variant="primary" size="2xl" radius="full" disabled={uploading}>
              {uploading ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default FeedbackForm;
